import { TriStateRange } from '~/_shared/constants/triStateRange.enum';
import { notNullsy } from '~/_shared/utils/typeGuards';
import type { BoundaryGroupId } from '~/store/boundaries/boundaryIdentifier.type';
import { BoundaryTerritoryType } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroup.type';
import type { BoundaryTerritoryGroup } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.state';
import type { BoundaryFilter } from '~/store/mapSettings/toolsState/boundary/mapSettingsToolsStateBoundary.state';
import type { BoundaryFilters } from '~/store/mapSettings/toolsState/boundaryTerritoryGroups/mapSettingsBTGToolsState.selectors';
import type {
  DataLayersFilterKey, DataLayersFiltersState,
} from './mapSettingsToolsStateDataLayers.state';

const emptyBoundaryFilter = {
  filteredBoundaries: [],
  filteredBoundaryTerritories: [],
  showAll: TriStateRange.Full,
} as const satisfies BoundaryFilter;

export const getDataLayersFilterKey = (
  { boundaryGroupId, settings: { boundaryTerritoryType, demographicId, bucketColumnId } }: Pick<BoundaryTerritoryGroup, 'boundaryGroupId'> & {
    settings: Pick<BoundaryTerritoryGroup['settings'], 'boundaryTerritoryType' | 'demographicId' | 'bucketColumnId'>;
  },
): DataLayersFilterKey => {
  const discriminator = boundaryTerritoryType === BoundaryTerritoryType.Demographic
    ? demographicId
    : bucketColumnId;
  const base: DataLayersFilterKey = `${boundaryGroupId}:${boundaryTerritoryType}`;

  return discriminator ? `${base}:${discriminator}` as DataLayersFilterKey : base;
};

export const isDataLayersFilterKey = (key: string): key is DataLayersFilterKey => /^\d+:[^:]+(:.+)?$/.test(key);

export const dataLayersFiltersStateToBoundaryFilters = (
  filters: DataLayersFiltersState,
  activeBoundaryTerritoryGroups: ReadonlyArray<BoundaryTerritoryGroup>,
): BoundaryFilters => {
  const filtersByBoundaryGroup = new Map<BoundaryGroupId, BoundaryFilter>();

  activeBoundaryTerritoryGroups.forEach(boundaryTerritoryGroup => {
    const filter = filters[getDataLayersFilterKey(boundaryTerritoryGroup)];
    if (filter) {
      filtersByBoundaryGroup.set(boundaryTerritoryGroup.boundaryGroupId, filter);
    }
  });

  return {
    get: (boundaryGroupId: BoundaryGroupId) => filtersByBoundaryGroup.get(boundaryGroupId) ?? emptyBoundaryFilter,
    entries: activeBoundaryTerritoryGroups
      .map(({ boundaryGroupId }) => {
        const filter = filtersByBoundaryGroup.get(boundaryGroupId);
        return filter ? { boundaryGroupId, filter } : null;
      })
      .filter(notNullsy),
  };
};
